import { Box, Flex, Input, Select, Stack } from '@chakra-ui/react';
import Button from '@molecules/Button';
import Card from '@molecules/Card';
import Text from '@molecules/Text';
import Calendar from '@view/components/Calendar';
import { useCallback, useState } from 'react';
import { setBaseEndpoint } from '@data/api/axios';
import { useStore } from '@data/other/withStore';
import { observer } from 'mobx-react';
import TimeUtils from '@business/utils/time';
import useAsyncAction from '@data/hooks/useAsyncAction';
import { AccommodationTypeList } from '@data/models';

const CalendarTestPage = () => {
  const endpointList = [
    { value: 'https://hotels.cloudbeds.com/', label: 'Production' },
    {
      value:
        'https://hotels.mfd-devbox-dustin-haggard-35803.cloudbeds-dev.com/',
      label: 'BE DevGA DevBox',
    },
  ];

  const [endpoint, setEndpoint] = useState<string>(endpointList[0].value);
  const [propertyId, setPropertyId] = useState<string>('');

  const {
    property: { availabilityCalendar, accommodations },
  } = useStore();

  const [
    loadAccommodationTypes,
    isLoadingAccomdationTypes,
    { data: accommodationTypeList },
  ] = useAsyncAction<AccommodationTypeList | undefined, [id?: number]>(
    useCallback(accommodations.loadAccommodationTypes, [])
  );

  const [searchAvailabilityCalendar, isSearchingAvailability] = useAsyncAction<
    void,
    [id: number, startDate: Date, endDate: Date]
  >(
    useCallback(async (propertyId, startDate, endDate) => {
      await availabilityCalendar.search({
        propertyId,
        startDate,
        endDate,
      });
    }, [])
  );

  function onSelectEndpoint(e: React.ChangeEvent<HTMLSelectElement>) {
    setEndpoint(e.currentTarget.value);
  }

  function parsePropertyURL() {
    let url = endpoint.trim();

    const isHttp = url.startsWith('http://');

    // remove http:// or https:// using regex
    url = url.replace(/(^\w+:|^)\/\//, '');

    // replace all # by ? in regex
    url = url.replace(/#(.*)/g, '?$1');

    //const propertyCode = url.split('/').pop() || '';

    const hostName = url.split('/')[0];
    const api = (isHttp ? 'http://' : 'https://') + hostName + '/booking/';
    setBaseEndpoint(api);

    const id = parseInt(propertyId.trim());

    loadAccommodationTypes(id);
    searchAvailabilityCalendar(
      id,
      TimeUtils.getToday(),
      TimeUtils.getDateOffset(0, 0, 2)
    );

    // using v3 api
    //property.get(propertyCode);
  }

  const loading = isSearchingAvailability || isLoadingAccomdationTypes;
  const calendarAvailable = (accommodationTypeList?.items.length || 0) > 0;

  return (
    <>
      <Flex direction="column" alignItems="center" mt="32px">
        <Stack gap="16px" maxW="740px">
          <Text type="h1">Calendar Test Page</Text>
          <Card>
            <Stack>
              <Flex gap="8px">
                <Stack flexGrow={1}>
                  <Text size="sm">API Base URL</Text>
                  <Select onChange={onSelectEndpoint}>
                    {endpointList.map((item, index) => {
                      return (
                        <option key={index} value={item.value}>
                          {item.label}
                        </option>
                      );
                    })}
                  </Select>
                </Stack>
                <Stack flexGrow={1}>
                  <Text size="sm">Property ID</Text>
                  <Input
                    placeholder="Please specify a property ID"
                    onChange={(e) => setPropertyId(e.currentTarget.value)}
                  />
                </Stack>
              </Flex>
              <Button
                color="primary"
                disabled={propertyId.trim().length === 0}
                isLoading={loading}
                onClick={() => parsePropertyURL()}
              >
                Apply
              </Button>
            </Stack>
          </Card>
          <Card position="relative">
            <Box position="relative">
              <Box
                opacity={calendarAvailable ? 1 : 0.3}
                pointerEvents={calendarAvailable ? 'unset' : 'none'}
              >
                <Calendar />
              </Box>
              {!calendarAvailable && (
                <Box position="absolute" w="100%" top="calc(50% - 48px)">
                  <Text textAlign="center" size="2xl">
                    Calendar not available
                  </Text>
                  <Text textAlign="center">Please specfy a property ID</Text>
                </Box>
              )}
            </Box>
          </Card>
        </Stack>
      </Flex>
    </>
  );
};

export default observer(CalendarTestPage);
